import React, { useEffect, useState } from "react";
import axios from "axios";
import { Mascot } from "./Mascot";
import { CringeMeter } from "./CringeMeter";
import { Crown, Trophy, Medal } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PODIUM_META = [
  { icon: Crown, bg: "#FF00FF", fg: "#fff", label: "GOAT (of being a clown)" },
  { icon: Trophy, bg: "#FFEA00", fg: "#000", label: "RUNNER UP" },
  { icon: Medal, bg: "#FF003C", fg: "#fff", label: "BRONZE DISASTER" },
];

export const HallOfShame = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/incidents/hall-of-shame?limit=10`)
      .then((r) => setItems(r.data))
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  const top3 = items.slice(0, 3);
  const rest = items.slice(3);

  return (
    <section
      id="hall-of-shame"
      className="py-20 relative bg-[#0a0a0a] brutal-border-y"
      data-testid="hall-of-shame-section"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <header className="mb-14">
          <span className="font-mono text-xs uppercase text-[#FF003C]">
            Chapter 02 · The Leaderboard
          </span>
          <h2 className="heading text-5xl md:text-7xl leading-[0.9] mt-2">
            THE <span className="bg-[#FFEA00] text-black px-2">HALL</span> OF
            SHAME
          </h2>
          <p className="mt-4 text-sm text-white/70 max-w-xl">
            Ranked by raw cringe + total community reactions. The higher you
            scroll, the dumber it gets.
          </p>
        </header>

        {loading ? (
          <div className="font-mono text-white/60">COMPUTING SHAME...</div>
        ) : (
          <>
            {/* PODIUM */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
              {top3.map((it, i) => {
                const meta = PODIUM_META[i];
                const Icon = meta.icon;
                // Translate for podium stepping
                const translate = i === 0 ? "md:-translate-y-4" : i === 2 ? "md:translate-y-4" : "";
                return (
                  <div
                    key={it.id}
                    className={`${translate}`}
                    data-testid={`podium-${i + 1}`}
                  >
                    <div
                      className="brutal-border bg-[#18181b] p-6 relative"
                      style={{ boxShadow: `8px 8px 0 0 ${meta.bg}` }}
                    >
                      <div
                        className="absolute -top-5 left-4 inline-flex items-center gap-2 px-3 py-1 brutal-border font-mono text-xs uppercase font-bold"
                        style={{ background: meta.bg, color: meta.fg, borderWidth: 3 }}
                      >
                        <Icon size={14} strokeWidth={3} />
                        RANK #{i + 1}
                      </div>
                      <div className="flex items-start gap-4 pt-4">
                        <Mascot size={88} crown={i === 0} shake={i === 0} ring={meta.bg} />
                        <div className="flex-1">
                          <span className="font-mono text-[11px] text-white/60 uppercase">
                            {it.date}
                          </span>
                          <h3 className="heading text-xl md:text-2xl text-white mt-1 leading-tight">
                            {it.title}
                          </h3>
                        </div>
                      </div>
                      <div className="mt-5">
                        <CringeMeter value={it.cringe} testId={`podium-cringe-${it.id}`} />
                      </div>
                      <div className="mt-4 flex justify-between font-mono text-[11px] text-white/70">
                        <span>FIRE {it.reactions?.fire ?? 0}</span>
                        <span>SKULL {it.reactions?.skull ?? 0}</span>
                        <span>LMAO {it.reactions?.laugh ?? 0}</span>
                      </div>
                      <p className="mt-4 text-sm text-white/70 line-clamp-3">
                        {it.description}
                      </p>
                      <div
                        className="mt-4 text-right font-mono text-[11px] text-black px-2 py-1 inline-block"
                        style={{ background: meta.bg }}
                      >
                        {meta.label}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* REST LIST */}
            <div className="brutal-border bg-black">
              <div
                className="grid grid-cols-12 font-mono text-[11px] uppercase px-4 py-2 bg-[#FFEA00] text-black"
                style={{ borderBottom: "4px solid #000" }}
              >
                <div className="col-span-1">#</div>
                <div className="col-span-6 md:col-span-5">Incident</div>
                <div className="hidden md:block md:col-span-2">Date</div>
                <div className="col-span-2 md:col-span-2">Cringe</div>
                <div className="col-span-3 md:col-span-2 text-right">Reactions</div>
              </div>
              <ul>
                {rest.map((it, i) => {
                  const rank = i + 4;
                  const total =
                    (it.reactions?.fire ?? 0) +
                    (it.reactions?.skull ?? 0) +
                    (it.reactions?.laugh ?? 0);
                  return (
                    <li
                      key={it.id}
                      data-testid={`shame-row-${rank}`}
                      className="grid grid-cols-12 items-center px-4 py-3 border-b border-white/10 hover:bg-[#120a0a] transition-colors duration-100"
                    >
                      <div className="col-span-1 font-mono text-[#FF003C] font-bold">
                        {rank}
                      </div>
                      <div className="col-span-6 md:col-span-5 heading text-base md:text-lg truncate pr-2">
                        {it.title}
                      </div>
                      <div className="hidden md:block md:col-span-2 font-mono text-xs text-white/60">
                        {it.date}
                      </div>
                      <div className="col-span-2 md:col-span-2">
                        <span
                          className="font-mono text-[11px] px-2 py-0.5"
                          style={{
                            background:
                              it.cringe >= 9
                                ? "#FF00FF"
                                : it.cringe >= 7
                                ? "#FF003C"
                                : it.cringe >= 4
                                ? "#FFEA00"
                                : "#00FF00",
                            color: it.cringe >= 9 ? "#fff" : "#000",
                          }}
                        >
                          {it.cringe}/10
                        </span>
                      </div>
                      <div className="col-span-3 md:col-span-2 text-right font-mono text-xs">
                        {total}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </>
        )}
      </div>
    </section>
  );
};
